import {
  AlertCircleIcon,
  GoogleIcon,
  Logout01Icon,
  Refresh01Icon,
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import * as React from 'react'

import { entrarComOutraConta, type FalhaToken, sair, urlDeLogin } from '@/lib/sessao'
import { useTituloDaPagina } from '@/lib/titulo'
import { perfis } from '@/lib/visoes'

import { FluxoVigiado } from '@/components/heimdall/fluxo-vigiado'
import { IconeTom } from '@/components/heimdall/icone-tom'
import { Logo } from '@/components/heimdall/logo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { BlocoExcecao } from '@/components/ui/bloco-excecao'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

/** Mensagem do painel antigo para conta fora do domínio (mantida igual). */
export const MENSAGEM_DOMINIO = 'Apenas contas @youxgroup.com.br podem acessar o painel.'

type Situacao =
  /** Tela de entrada normal: botão do Google. */
  | { tipo: 'entrar'; destino: string; semAcesso?: boolean }
  /** Entrou com uma conta fora do domínio. */
  | { tipo: 'sem-acesso'; email: string }
  /** A API não liberou o acesso (falha ao trocar o e-mail pelo token). */
  | { tipo: 'falha'; falha: FalhaToken }

/** Lado vinho da tela: o fluxo de trabalho sendo vigiado e a frase do produto. */
function LadoDaMarca() {
  return (
    <section className="heimdall-block-primary flex flex-col gap-6 overflow-hidden p-5 md:gap-8 md:p-10">
      <div className="flex flex-1 items-center">
        <FluxoVigiado className="mx-auto max-w-2xl text-primary-foreground" />
      </div>
      <div className="flex max-w-lg flex-col gap-3">
        <h2 className="text-2xl leading-tight font-semibold tracking-tight text-balance md:text-4xl">
          Processo claro, entrega sem surpresa.
        </h2>
        <p className="hidden text-sm text-primary-foreground/80 sm:block md:text-base">
          O Heimdall acompanha tarefas, branches e MRs no GitLab e orienta o time na hora certa —
          sem bloquear ninguém.
        </p>
      </div>
    </section>
  )
}

/** Botão principal grande (pílula de 44 px), com estado de "indo para o Google". */
function BotaoGoogle({
  rotulo,
  aoClicar,
}: {
  rotulo: string
  aoClicar: () => void | Promise<void>
}) {
  const [indo, setIndo] = React.useState(false)
  return (
    <Button
      size="lg"
      className="heimdall-botao h-11 w-full rounded-full"
      disabled={indo}
      onClick={() => {
        setIndo(true)
        void aoClicar()
      }}
    >
      {indo ? (
        <Spinner data-icon="inline-start" />
      ) : (
        <HugeiconsIcon icon={GoogleIcon} strokeWidth={2} data-icon="inline-start" />
      )}
      {indo ? 'Conectando ao Google…' : rotulo}
    </Button>
  )
}

/** O que deu errado ao liberar o acesso, em palavras simples, por etapa da falha. */
const MOTIVO_DA_FALHA: Record<FalhaToken['tipo'], string> = {
  conexao: 'O login com o Google deu certo, mas o Heimdall não respondeu.',
  status: 'O login com o Google deu certo, mas o Heimdall recusou o pedido de acesso.',
  json: 'O login com o Google deu certo, mas o Heimdall devolveu uma resposta que não deu para ler.',
  sem_token: 'O login com o Google deu certo, mas o Heimdall não devolveu a liberação de acesso.',
}

/**
 * Tela de entrada do Heimdall. Também é a tela de bloqueio: conta fora do
 * domínio @youxgroup.com.br ou falha da API ao liberar o acesso.
 * O login em si continua no servidor Bun (`/auth/login` → Google → `/oauth2callback`).
 */
export function TelaEntrada({ situacao }: { situacao: Situacao }) {
  useTituloDaPagina(situacao.tipo === 'entrar' ? 'Entrar' : 'Sem acesso')
  const semAcesso =
    situacao.tipo === 'sem-acesso' || (situacao.tipo === 'entrar' && situacao.semAcesso)

  return (
    <div className="heimdall-ground grid min-h-svh gap-3 p-3 lg:grid-cols-[1.25fr_1fr]">
      <LadoDaMarca />

      <main className="heimdall-block flex flex-col items-center justify-center p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <Logo tamanho="lg" />
            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl font-semibold tracking-tight">
                {situacao.tipo === 'falha'
                  ? 'Não deu para liberar seu acesso'
                  : 'Entrar no Heimdall'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {situacao.tipo === 'falha'
                  ? MOTIVO_DA_FALHA[situacao.falha.tipo]
                  : 'Use sua conta Google da Youx para continuar.'}
              </p>
            </div>
          </div>

          {semAcesso ? (
            <Alert
              variant="error"
              className="gap-1 rounded-2xl border border-error/20 p-4 text-foreground *:data-[slot=alert-description]:text-muted-foreground"
            >
              <HugeiconsIcon icon={AlertCircleIcon} strokeWidth={2} className="size-5" />
              <AlertTitle className="font-semibold">Esta conta não tem acesso</AlertTitle>
              <AlertDescription className="flex flex-col gap-1.5 [&_p:not(:last-child)]:mb-0">
                {situacao.tipo === 'sem-acesso' ? (
                  <p>
                    Você entrou como{' '}
                    <span className="font-medium break-all text-foreground">{situacao.email}</span>.
                  </p>
                ) : null}
                <p>{MENSAGEM_DOMINIO}</p>
                <p>Entre com o e-mail da Youx. Se não der certo, peça acesso a quem administra.</p>
              </AlertDescription>
            </Alert>
          ) : null}

          {situacao.tipo === 'falha' ? (
            <BlocoExcecao
              titulo="O Heimdall não liberou o acesso"
              mensagem="Tente de novo em alguns instantes. Se continuar, avise quem cuida do Heimdall e envie os detalhes abaixo."
              detalhe={`${situacao.falha.excecao}: ${situacao.falha.mensagem}`}
            />
          ) : null}

          <div className="flex flex-col gap-3">
            {situacao.tipo === 'entrar' && !situacao.semAcesso ? (
              <BotaoGoogle
                rotulo="Entrar com Google"
                aoClicar={() => window.location.assign(urlDeLogin(situacao.destino))}
              />
            ) : null}
            {semAcesso ? (
              <BotaoGoogle
                rotulo="Entrar com outra conta"
                aoClicar={() =>
                  entrarComOutraConta(situacao.tipo === 'entrar' ? situacao.destino : undefined)
                }
              />
            ) : null}
            {situacao.tipo === 'falha' ? (
              <Button
                size="lg"
                className="heimdall-botao h-11 w-full rounded-full"
                onClick={() => window.location.reload()}
              >
                <HugeiconsIcon icon={Refresh01Icon} strokeWidth={2} data-icon="inline-start" />
                Tentar de novo
              </Button>
            ) : null}
            {situacao.tipo !== 'entrar' ? (
              <Button
                variant="outline"
                size="lg"
                className="h-11 w-full rounded-full"
                onClick={() => void sair()}
              >
                <HugeiconsIcon icon={Logout01Icon} strokeWidth={2} data-icon="inline-start" />
                Sair
              </Button>
            ) : null}
            <p className="text-center text-xs text-muted-foreground">
              Acesso liberado só para e-mails @youxgroup.com.br.
            </p>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl bg-muted/60 p-4">
            <div className="flex flex-col gap-0.5 text-center">
              <p className="text-sm font-medium">Cada pessoa vê o que precisa</p>
              <p className="text-xs text-muted-foreground">
                O Heimdall abre na visão do seu papel. Para mudar, fale com quem administra.
              </p>
            </div>
            <ul className="grid grid-cols-3 gap-2">
              {perfis.map((perfil) => (
                <li
                  key={perfil.id}
                  className="flex flex-col items-center gap-1.5 rounded-xl bg-card px-1.5 py-2.5 text-center"
                >
                  <IconeTom icone={perfil.icone} tom={perfil.tom} tamanho="sm" />
                  <span className="text-xs leading-tight font-medium">{perfil.nome}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  )
}
